import { moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, input, OnChanges, OnInit, output, SimpleChanges } from '@angular/core';

import { MatTableDataSource } from '@angular/material/table';
import { translate } from '@jsverse/transloco';
import { Icons, LRPickerInputObject, TabObject } from '@inmoment/the-kitchen';

import {
  DocumentsQuickActionService,
  QuickActionInputFieldEnum,
  QuickActionsItem,
} from 'app/modules/collection/services/documents-quick-action.service';
import { WidgetDocumentListComponent } from 'app/modules/widget/components/widget-document-list/widget-document-list.component';
import { FieldService } from 'app/providers/storage-api/field.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { DocumentListColumnSettings } from '../document-list/document-list.component';

export interface DocumentListSettingsDialog {
  textFields: DocumentListColumnSettings[];
  otherFields: DocumentListColumnSettings[];
  isClickOpenDocument: boolean;
  columnDisplayLabels: { [key: string]: string };
  useFieldNames: { [key: string]: boolean };
  showNlpData: boolean;
  showMetadata: boolean;
  showQuickActions?: boolean;
  quickActions?: QuickActionsItem[];
  documentListWidget: WidgetDocumentListComponent;
}

export interface DocumentListSettingsDialogOptions extends DocumentListSettingsDialog {
  availableNlpFields: { id; title; disabled?; display? }[];
  availableMetadataFields: { id; title; disabled?; display? }[];
}

export interface DocumentListSettingsDialogData extends DocumentListSettingsDialog {
  nlpFields: { id; display }[];
  metadataFields: { id; display }[];
}

@EnableClassTelemetry()
@Component({
  selector: 'app-document-list-settings-dialog',
  templateUrl: './document-list-settings-dialog.component.html',
  styleUrls: ['./document-list-settings-dialog.component.scss'],
  standalone: false,
})
export class DocumentListSettingsDialogComponent implements OnInit, OnChanges {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly options = input<DocumentListSettingsDialogOptions>();
  readonly isReadOnly = input<boolean>(false);
  readonly modalClosed = output<DocumentListSettingsDialogData>();
  tabs: TabObject[] = [
    { title: translate('documents.list-settings.field-selection'), id: 'columns' },
    { title: translate('documents.list-settings.field-labels'), id: 'labels' },
    { title: translate('documents.list-settings.modal-options'), id: 'viewer' },
    { title: translate('documents.list-settings.card-options'), id: 'card' },
  ];
  activeTab = 'columns';

  columnPickerData: LRPickerInputObject = {
    initialAvailableItems: [],
    initialSelectedItems: [],
  };
  selectedColumns: any[];

  fieldLabelsColumns = ['name', 'label'];
  fieldLabelsDs: MatTableDataSource<{
    columnId: string;
    fieldName: string;
    displayLabel: string;
    useFieldDisplayName: boolean;
    isCustomField: boolean;
  }>;

  isClickOpenDocument = true;
  showNlpData = true;
  nlpFields: { id; title; disabled?; display? }[];
  showMetadata = true;
  metadataPickerData: LRPickerInputObject = {
    initialAvailableItems: [],
    initialSelectedItems: [],
  };
  availableMetadata: any[];
  selectedMetadata: any[];

  listOfIcons: Icons[] = [];
  listOfConfiguredIcons: Icons[] = [];
  showQuickActions = true;
  defaultQuickActions: QuickActionsItem[] = [];
  quickActionErrorMessage = {
    required: translate('documents.list-settings.quick-actions.error-required'),
    duplicateTitle: translate('documents.list-settings.quick-actions.error-duplicate-title'),
    duplicateTag: translate('documents.list-settings.quick-actions.error-duplicate-tag'),
  };
  configuredQuickActions: QuickActionsItem[] = [];
  copyOfConfiguredQuickActions: QuickActionsItem[] = [];
  activeQuickActionsLabel: string;
  activeQuickActionsCount: number;
  quickActionNameError: string;
  quickActionTagError: string;
  isEditMode = false;

  formValidity = {
    isValid: true,
    errorMessage: undefined,
  };

  constructor(private documentsQuickActionService: DocumentsQuickActionService) {
    this.defaultQuickActions = this.documentsQuickActionService.defaultQuickActions;
    this.listOfIcons = this.documentsQuickActionService.listOfIcons;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isReadOnly']) {
      this.setTabAccessibility();
    }
  }

  private setTabAccessibility(): void {
    if (this.isReadOnly()) {
      this.tabs = this.tabs.map((tab) =>
        tab.id === 'labels' || tab.id === 'card' ? { ...tab, disabled: true } : tab
      );
      if (this.activeTab === 'labels' || this.activeTab === 'card') {
        this.activeTab = 'columns';
      }
      return;
    }

    this.tabs = this.tabs.map((tab) => ({ ...tab, disabled: false }));
  }

  ngOnInit() {
    this.setTabAccessibility();
    this.initializeFieldSelectionTab();

    this.fieldLabelsDs = new MatTableDataSource(
      this.selectedColumns.map((f) => ({
        columnId: f.id,
        fieldName: f.title,
        displayLabel: this.options().useFieldNames?.[f.id]
          ? FieldService.getFieldLongName(f.id.split('.')[1])
          : this.options().columnDisplayLabels?.[f.id] || f.title,
        useFieldDisplayName: !!this.options().useFieldNames?.[f.id],
        isCustomField: FieldService.isCustomField(f.id.split('.')[1]),
      }))
    );

    this.initializeModalOptionsTab();
    this.initializeCardOptionsTab();
  }

  private initializeFieldSelectionTab() {
    this.columnPickerData.initialAvailableItems = [
      ...this.options().textFields,
      ...this.options().otherFields,
    ]
      .filter((f) => !f.display)
      .map((f) => ({ title: f.label, id: f.columnId }));

    this.columnPickerData.initialSelectedItems = [
      ...this.options().textFields,
      ...this.options().otherFields,
    ]
      .filter((f) => f.display)
      .map((f) => ({ title: f.label, id: f.columnId }));
    this.selectedColumns = [...this.columnPickerData.initialSelectedItems];
  }

  private initializeModalOptionsTab() {
    this.isClickOpenDocument = this.options().isClickOpenDocument ?? true;
    this.showNlpData = this.options().showNlpData ?? true;
    this.showMetadata = this.options().showMetadata ?? true;

    this.nlpFields = this.options().availableNlpFields;

    this.metadataPickerData.initialAvailableItems = this.options().availableMetadataFields.filter(
      (f) => !f.display
    );
    this.availableMetadata = [...this.metadataPickerData.initialAvailableItems];

    this.metadataPickerData.initialSelectedItems = this.options().availableMetadataFields.filter(
      (f) => f.display
    );
    this.selectedMetadata = [...this.metadataPickerData.initialSelectedItems];
  }

  private initializeCardOptionsTab() {
    this.showQuickActions = this.options().showQuickActions ?? true;
    this.configuredQuickActions = this.mapQuickActionsPayload(
      this.options().quickActions?.length ? this.options().quickActions : this.defaultQuickActions
    );
    this.copyOfConfiguredQuickActions = this.configuredQuickActions;

    this.listOfConfiguredIcons = this.getListOfConfiguredIcons();
    this.getActiveQuickActions();
  }

  private getActiveQuickActions(): void {
    this.activeQuickActionsCount =
      this.configuredQuickActions.filter((q) => !!q.enabled)?.length ?? 0;

    this.activeQuickActionsLabel = `${this.activeQuickActionsCount} / ${
      this.configuredQuickActions.length
    } ${translate('documents.list-settings.quick-actions.active-quick-actions')}`;
  }

  private getListOfConfiguredIcons(): Icons[] {
    return this.configuredQuickActions.map((c) => c.icon as Icons);
  }

  private getQuickActionError(
    isEmpty: boolean,
    hasDuplicate: boolean,
    key: QuickActionInputFieldEnum
  ): string {
    if (isEmpty) {
      return this.quickActionErrorMessage.required;
    }

    if (hasDuplicate) {
      if (key === QuickActionInputFieldEnum.TAG) {
        return this.quickActionErrorMessage.duplicateTag;
      }
      return this.quickActionErrorMessage.duplicateTitle;
    }
  }

  private resetQuickActionVars(): void {
    this.copyOfConfiguredQuickActions = this.configuredQuickActions;
    this.getActiveQuickActions();
    this.isEditMode = false;
    this.quickActionNameError = undefined;
    this.quickActionTagError = undefined;
    this.listOfConfiguredIcons = this.getListOfConfiguredIcons();
  }

  private mapQuickActionsPayload(quickActions: QuickActionsItem[]): QuickActionsItem[] {
    return quickActions?.map(
      (qa) =>
        <QuickActionsItem>{
          id: qa.id,
          title: qa.title,
          enabled: qa.enabled,
          icon: qa.icon,
          isEditable: qa.isEditable,
          tag: qa.tag,
        }
    );
  }

  onColumnSelectionChanged($event) {
    this.selectedColumns = $event.selectedItems;

    this.fieldLabelsDs.data = this.fieldLabelsDs.data.filter((f) =>
      this.selectedColumns.some((item) => item.id === f.columnId)
    );

    this.selectedColumns.forEach((item, idx) => {
      const dsIdx = this.fieldLabelsDs.data.findIndex((f) => f.columnId === item.id);
      if (dsIdx === -1) {
        const isCustomField = FieldService.isCustomField(item.id.split('.')[1]);
        this.fieldLabelsDs.data.splice(idx, 0, {
          columnId: item.id,
          fieldName: item.title,
          displayLabel: item.title,
          useFieldDisplayName: isCustomField,
          isCustomField,
        });
      } else if (dsIdx !== idx) {
        moveItemInArray(this.fieldLabelsDs.data, dsIdx, idx);
      }
    });

    this.fieldLabelsDs.data = [...this.fieldLabelsDs.data];

    this.setValidity();
  }

  onNlpFieldsReordered($event) {
    moveItemInArray(this.nlpFields, $event.previousIndex, $event.currentIndex);
  }

  onMetadataSelectionChanged($event) {
    this.availableMetadata = $event.availableItems;
    this.selectedMetadata = $event.selectedItems;
  }

  onQuickActionsReordered(event) {
    moveItemInArray(this.configuredQuickActions, event.previousIndex, event.currentIndex);
  }

  onAddAction(): void {
    this.isEditMode = true;
    this.configuredQuickActions.forEach((action) => {
      action.isEditMode = false;
    });

    const newAction = {
      id: undefined,
      title: '',
      tag: '',
      enabled: this.activeQuickActionsCount < 6,
      icon: undefined,
      isEditable: true,
      isEditMode: true,
    };

    this.configuredQuickActions.push(newAction);
  }

  onQuickActionEdit(item: QuickActionsItem): void {
    this.isEditMode = true;
    this.configuredQuickActions = this.configuredQuickActions.filter((c) => !!c.id);

    this.configuredQuickActions.forEach((action) => {
      action.isEditMode = action.id === item.id;
    });
  }

  onQuickActionCheckChange(index: number, isEnabled: boolean): void {
    if (this.configuredQuickActions[index]) {
      this.configuredQuickActions[index].enabled = isEnabled;
    }
    this.getActiveQuickActions();
  }

  onQuickActionIconSelect(index: number, icon: Icons): void {
    if (this.configuredQuickActions[index]) {
      this.configuredQuickActions[index].icon = icon;
    }
    this.listOfConfiguredIcons = this.getListOfConfiguredIcons();
  }

  onQuickActionValueChange(value: string, actionId: string, key: string): void {
    const trimmedValue = value?.trim().toLowerCase();
    const hasDuplicate = this.copyOfConfiguredQuickActions.some(
      (action) =>
        action.id !== actionId && !!trimmedValue && action[key]?.toLowerCase() === trimmedValue
    );

    if (key === QuickActionInputFieldEnum.TITLE) {
      this.quickActionNameError = this.getQuickActionError(
        !trimmedValue,
        hasDuplicate,
        QuickActionInputFieldEnum.TITLE
      );
    } else if (key === QuickActionInputFieldEnum.TAG) {
      this.quickActionTagError = this.getQuickActionError(
        !trimmedValue,
        hasDuplicate,
        QuickActionInputFieldEnum.TAG
      );
    }
  }

  onQuickActionDelete(item: QuickActionsItem): void {
    this.configuredQuickActions = this.configuredQuickActions.filter((q) => q.id !== item.id);
    this.resetQuickActionVars();
  }

  onQuickActionSave(item: QuickActionsItem): void {
    item.isEditMode = false;
    item.title = item.title?.trim();
    item.tag = item.tag?.trim();
    item.id = item.id ?? `action-id-${this.configuredQuickActions.length + 1}`;

    /** if no icon is selected, auto save the first unused icon from list **/
    if (!item.icon) {
      const icons = this.listOfConfiguredIcons;
      item.icon = this.listOfIcons.find((i) => !icons.some((icon) => icon === i));
    }

    this.resetQuickActionVars();
  }

  setValidity() {
    const hasSelection = !!this.selectedColumns.length;
    const hasLabels = this.fieldLabelsDs.data.every((f) => f.displayLabel);

    this.formValidity.isValid = hasSelection && hasLabels;
    this.formValidity.errorMessage = undefined;

    if (!hasSelection) {
      this.formValidity.errorMessage = translate('documents.list-settings.error-select-field');
    }

    if (!hasLabels) {
      this.formValidity.errorMessage = translate('documents.list-settings.error-field-labels');
    }
  }

  onUseFieldDisplayNameChange(element, isChecked: boolean) {
    const fieldName = element.columnId.split('.')[1];
    if (isChecked) {
      element.displayLabel = FieldService.getFieldLongName(fieldName);
    }
  }

  onDisplayNameChange(element) {
    this.setValidity();
    const fieldName = element.columnId.split('.')[1];
    const isDisplayNameEqualToFieldNameLongName =
      element.displayLabel === FieldService.getFieldLongName(fieldName);
    element.useFieldDisplayName = isDisplayNameEqualToFieldNameLongName;
  }

  cancel() {
    this.modalClosed.emit(null);
  }

  save() {
    const prepareResponseFields = (fields) => {
      const responseFields = [...fields];

      responseFields.forEach(
        (rf) => (rf.display = this.selectedColumns.some((i) => i.id === rf.columnId))
      );
      responseFields.sort(
        (f1, f2) =>
          this.selectedColumns.findIndex((i) => i.id === f1.columnId) -
          this.selectedColumns.findIndex((i) => i.id === f2.columnId)
      );

      return responseFields;
    };

    const metadataFields = [
      ...this.availableMetadata.map((f) => ({ id: f.id, display: false })),
      ...this.selectedMetadata.map((f) => ({ id: f.id, display: true })),
    ];
    const oldTagsIdx = metadataFields.findIndex((f) => f.id === 'tags');
    const [tagElement] = metadataFields.splice(oldTagsIdx, 1);
    let newTagsIdx = this.availableMetadata.length;
    if (tagElement.display) {
      newTagsIdx += this.selectedMetadata.length;
    }
    metadataFields.splice(newTagsIdx - 1, 0, tagElement);

    const quickActions = this.mapQuickActionsPayload(
      this.configuredQuickActions.length ? this.configuredQuickActions : this.defaultQuickActions
    );

    this.modalClosed.emit({
      textFields: prepareResponseFields(this.options().textFields),
      otherFields: prepareResponseFields(this.options().otherFields),
      columnDisplayLabels: this.fieldLabelsDs.data.reduce((prev, curr) => {
        prev[curr.columnId] = curr.displayLabel;
        return prev;
      }, {}),
      useFieldNames: this.fieldLabelsDs.data.reduce((prev, curr) => {
        prev[curr.columnId] = curr.useFieldDisplayName;
        return prev;
      }, {}),
      isClickOpenDocument: this.isClickOpenDocument,
      showNlpData: this.showNlpData,
      nlpFields: this.nlpFields.map((f) => ({ id: f.id, display: f.display })),
      showMetadata: this.showMetadata,
      metadataFields,
      documentListWidget: this.options().documentListWidget,
      showQuickActions: this.showQuickActions,
      quickActions,
    });
  }
}
